import { withLiteObserverAndTheme } from "../et3-enhanced-render";
import {
  DataGrid,
  GridAlignment,
  GridCellParams,
  GridColDef,
  GridRowClassNameParams,
  GridToolbar
} from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { isPositiveNumber, reduce } from "et3-type-guards";
import EmptyTable from "./EmptyTable";
import TableTitle from "./TableTitle";
import { Gender, TAdminTable } from "../../Types";
import { GENDER_COLORED_TABLE_NAMES } from "../../constants/input-entries-constants";
import getCoreHistoryStore from "../../stores/core/core-history-store";
import React from "react";
import { apiCallLogAdminGeneratedTables } from "../../api/core-api/api-call-log-admin-generated-tables";

/**
 * Enhanced DataGrid component with theming and customization options.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {GridColDef[]} props.columns - Column definitions for the table.
 * @param {Object[]} props.rows - Data rows for the table.
 * @param {string} props.title - Title of the table.
 * @param {boolean} props.shouldBeFlagged - Determines border color based on flag.
 * @param {(params: GridRowClassNameParams) => string} [props.getRowClassName] - Function to return row class name.
 * @param {(params: GridCellParams) => string} [props.getCellClassName] - Function to return cell class name.
 * @returns {JSX.Element} The rendered Et3Table component.
 */
const Et3Table = withLiteObserverAndTheme<{
  columns: GridColDef[];
  rows: Record<string, any>[];
  title: string;
  titleKey: TAdminTable; // Temporary Prop... will removed once feature is deprecated
  shouldBeFlagged: boolean;
  getRowClassName?: (params: GridRowClassNameParams) => string;
  getCellClassName?: (params: GridCellParams) => string;
}>((props) => {
  const {
    columns,
    rows,
    title,
    titleKey,
    shouldBeFlagged,
    theme,
    getRowClassName,
    getCellClassName
  } = props;
  const { colors, unitX } = theme;
  const tableBorderColor = shouldBeFlagged
    ? colors.all.red
    : colors.all.bondiBlue;
  const tableRef = React.useRef<HTMLDivElement>(null);
  const hasBeenSeen = React.useRef(false);

  React.useEffect(() => {
    hasBeenSeen.current = false;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting && !hasBeenSeen.current) {
          await apiCallLogAdminGeneratedTables({
            subTab: getCoreHistoryStore().getPageName(),
            subTabTable: titleKey
          });
          hasBeenSeen.current = true;
          observer.disconnect();
        }
      },
      {
        root: null,
        threshold: 0
      }
    );

    const tableContainer = tableRef.current;

    if (tableContainer) {
      // Wait until the ET3Table rows are rendered, then observe the first visible row
      requestAnimationFrame(() => {
        const firstRowVisible =
          tableContainer.querySelector(".MuiDataGrid-row");
        if (firstRowVisible) {
          observer.observe(firstRowVisible);
        }
      });
    }

    return () => observer.disconnect();
  }, [rows]);

  /**
   * Computes column configurations dynamically.
   * @returns {GridColDef[]} Configured column definitions.
   */
  const calculatedColumns = columns.map((column) => {
    let textcounter = 0;
    const conditionSum = columns.reduce((accumulator, currentValue) => {
      if (isPositiveNumber(currentValue.width)) {
        return accumulator + currentValue.width;
      }
      textcounter = textcounter + 1;
      return accumulator;
    }, 0);
    const widthValue = isPositiveNumber(textcounter)
      ? (1500 * unitX - conditionSum) / textcounter
      : 1500 * unitX;
    return {
      ...column,
      headerAlign: "center" as GridAlignment,
      align: "center" as GridAlignment,
      flex: reduce(() => column.flex, 1),
      width: widthValue,
      sortable: false,
      filterable: true
    };
  });

  /**
   * Adds unique IDs to rows.
   * @returns {Object[]} Rows with unique identifiers.
   */
  const rowsWithIds = rows.map((row, index) => ({
    ...row,
    uniqueId: index
  }));

  /**
   * Default function to determine row class name.
   * @param {object} params - Row parameters.
   * @returns {string} Computed class name for the row.
   */
  const defaultRowClassName = (params: object) => {
    // @ts-ignore
    const { gender } = params.row;
    const senderGender = reduce(() => gender, "");
    const pageName = getCoreHistoryStore().pageName.get();
    const pageCondition = GENDER_COLORED_TABLE_NAMES.includes(pageName);
    if (!pageCondition) {
      return "table-default-row-color";
    } else if (senderGender === Gender.Male) {
      return "table-messages-male-row-color";
    } else if (senderGender === Gender.Female) {
      return "table-messages-female-row-color";
    }
    return "table-default-row-color";
  };

  return !isPositiveNumber(rows.length) ? (
    <EmptyTable width={1500} title={title} />
  ) : (
    <Box
      ref={tableRef}
      sx={{
        backgroundColor: `${colors.all.white}`,
        border: `3px solid ${tableBorderColor}`,
        borderRadius: 10,
        textAlign: "center"
      }}
    >
      <TableTitle width={1500} title={title} />
      <DataGrid
        rows={rowsWithIds}
        getRowId={(row: { uniqueId: number }) => row.uniqueId}
        columns={calculatedColumns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 100
            }
          }
        }}
        //so now we can have 100 rows selected and more in this table
        pageSizeOptions={[50, 100]}
        checkboxSelection={false}
        disableRowSelectionOnClick={true}
        slots={{ toolbar: GridToolbar }}
        getRowHeight={() => "auto"}
        getCellClassName={getCellClassName || (() => "table-cell-class")}
        getRowClassName={getRowClassName || defaultRowClassName}
        showColumnVerticalBorder
        showCellVerticalBorder
        style={{
          textAlign: "center",
          justifyContent: "center",
          border: `1px solid ${colors.all.transparent}`
        }}
        autoHeight
        columnBuffer={5}
        sx={{
          ".MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            wordBreak: "break-all",
            lineHeight: "20px",
            overflow: "auto"
          },
          ".MuiDataGrid-columnHeaders": {
            maxHeight: "200px !important"
          }
        }}
      />
    </Box>
  );
});
export default Et3Table;
